import {observable} from 'mobx';
import GeneralSelectorTableStore, {NoneKPIColumns, SelectorTableColumnDef} from '../GeneralSelectorTableStore';
import {SelectorTableLayout} from '../SelectorTableLayout';
import {numberFormat, percentFormat, textFormat} from '../../utility/FormatValueUtils';
import {AnalyticsTab, Store} from '../store';
import {CampaignKPI, KPIGroupKey, SessionInsightKPI} from '../../kpi';
import {TableDataForChannel} from '../../types/api/tableData/TableDataForChannel';
import {MessageProvider} from '../../i18n/i18n';
import {Channel} from '../../types/Channel';
import {ChannelState} from '../ChannelStore';
import {TimeState} from '../TimeIntervalStore';
import {Omit} from '../../types/Utility';
import {TableSort, TableSortDirection} from '../../types/Table';

type SelectorTableColumnDefWithoutLabel = Omit<SelectorTableColumnDef, 'label' | 'width'>;

class CampaignSelectorTableStore extends GeneralSelectorTableStore {
    constructor(mainStore: Store, t: MessageProvider, protected readonly channelState: ChannelState, timeState: TimeState) {
        super(mainStore, t, channelState, timeState);
        this.updateTableSettingsFromLocalStorage('campaign');
    }

    protected dependantAnalyticsTab: AnalyticsTab = AnalyticsTab.CampaignView;

    public fetchInsightsData = () => {
        /* no op*/
    };

    public readonly defaultSorting: TableSort = {
        sortDirection: TableSortDirection.DESC,
        attribute: CampaignKPI.Sales,
    };

    public tableSorting: TableSort = {...this.defaultSorting};

    private getNoneNumberColums(): SelectorTableColumnDefWithoutLabel[] {
        return [
            {
                dataKey: NoneKPIColumns.RowNumber,
                minWidth: 50,
                align: 'right',
                isSortable: false,
                valueFormat: textFormat(),
            },
            {
                dataKey: CampaignKPI.Name,
                minWidth: 300,
                align: 'left',
                withRevealOnHover: true,
                valueFormat: textFormat(),
            },
            {
                dataKey: KPIGroupKey.CampaignId,
                minWidth: 150,
                align: 'left',
                valueFormat: textFormat(),
                withRevealOnHover: true,
            },
        ];
    }

    public get tableColumnList(): SelectorTableColumnDef[] {
        return this.resizeColumns(
            this.withI18N([
                ...this.getNoneNumberColums(),
                {
                    dataKey: CampaignKPI.Category,
                    minWidth: 120,
                    align: 'left',
                    valueFormat: textFormat(),
                    withRevealOnHover: true,
                },
                {
                    dataKey: CampaignKPI.Flavour,
                    minWidth: 130,
                    align: 'left',
                    valueFormat: textFormat(),
                },
                {
                    dataKey: CampaignKPI.AvgPushedProductsCount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.SearchCount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.UniqueSearchCount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.ClickCount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.UniqueClickCount,
                    sessionApiKey: SessionInsightKPI.CartTotalAmount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.CartCount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.CheckoutCount,
                    sessionApiKey: SessionInsightKPI.CheckoutTotalAmount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.Sales,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: this.getMoneyFormatter(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.ClickRate,
                    minWidth: 140,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: percentFormat(),
                    isChartable: true,
                    withFilter: true,
                    isRate: true,
                },
                {
                    dataKey: CampaignKPI.UniqueClickRate,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: percentFormat(),
                    isChartable: true,
                    withFilter: true,
                    isRate: true,
                },
                {
                    dataKey: CampaignKPI.BounceRate,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: percentFormat(),
                    isChartable: true,
                    withFilter: true,
                    isRate: true,
                },
                {
                    dataKey: CampaignKPI.ExitRate,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: percentFormat(),
                    isChartable: true,
                    withFilter: true,
                    isRate: true,
                },
                {
                    dataKey: CampaignKPI.ClickToCartRate,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: percentFormat(),
                    isChartable: true,
                    withFilter: true,
                    isRate: true,
                },
                {
                    dataKey: CampaignKPI.ClickToBuyRate,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: percentFormat(),
                    isChartable: true,
                    withFilter: true,
                    isRate: true,
                },
                {
                    dataKey: CampaignKPI.CheckoutPerSearchRate,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: percentFormat(),
                    isChartable: true,
                    withFilter: true,
                    isRate: true,
                },
                {
                    dataKey: CampaignKPI.CartToCheckoutRate,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: percentFormat(),
                    isChartable: true,
                    withFilter: true,
                    isRate: true,
                },
                {
                    dataKey: CampaignKPI.SalesPerSearch,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: this.getMoneyFormatter(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.SalesPerClick,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: this.getMoneyFormatter(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: CampaignKPI.AvgCartValue,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: this.getMoneyFormatter(),
                    isChartable: true,
                    withFilter: true,
                },
            ])
        );
    }

    public set tableColumnList(_: SelectorTableColumnDef[]) {
        // Dummy
    }

    public readonly layout = new SelectorTableLayout(
        this.tableColumns,
        () => this.mainStore.compareStore,
        [
            {
                name: 'fixed',
                label: '',
                columns: [NoneKPIColumns.RowNumber, CampaignKPI.Name, KPIGroupKey.CampaignId],
            },
            {
                name: 'misc',
                label: this.t('table:table.misc'),
                columns: [CampaignKPI.Category, CampaignKPI.Flavour],
            },
            {
                name: 'funnel',
                label: this.t('table:table.funnel'),
                columns: [
                    CampaignKPI.SearchCount,
                    CampaignKPI.UniqueSearchCount,
                    CampaignKPI.ClickCount,
                    CampaignKPI.UniqueClickCount,
                    CampaignKPI.CartCount,
                    CampaignKPI.CheckoutCount,
                    CampaignKPI.AvgPushedProductsCount,
                ],
            },
            {
                name: 'rates',
                label: this.t('table:table.rates'),
                columns: [
                    CampaignKPI.ClickRate,
                    CampaignKPI.UniqueClickRate,
                    CampaignKPI.BounceRate,
                    CampaignKPI.ExitRate,
                    CampaignKPI.ClickToCartRate,
                    CampaignKPI.ClickToBuyRate,
                    CampaignKPI.CheckoutPerSearchRate,
                    CampaignKPI.CartToCheckoutRate,
                ],
            },
            {
                name: 'salesKpis',
                label: this.t('table:table.salesKpis'),
                columns: [CampaignKPI.SalesPerSearch, CampaignKPI.SalesPerClick, CampaignKPI.AvgCartValue, CampaignKPI.Sales],
            },
        ],
        this.dependantAnalyticsTab,
        this.saveTableLayoutToLocalStorage('campaign'),
        this.getTableLayoutFromLocalStorage('campaign'),
        this.recomputeMultiGridSizeIfPossible
    );

    // table cell drilldown session modal
    @observable public cellAttributeSessionModal = null;
    @observable public sessionsForPrimaryAndSecondaryAttribute = [];
    @observable public selectedSessionForPrimaryAndSecondaryAttribute = null;
    @observable public sessionDataForPrimaryAndSecondaryAttribute = [];

    public calculateRatesForStickySumRow(data: TableDataForChannel, channel: Channel, searchterm: string): TableDataForChannel {
        const newData = {...data, [NoneKPIColumns.RowNumber]: '#'};
        newData[KPIGroupKey.CampaignId] = '#';
        newData[CampaignKPI.Category] = '#';
        newData[CampaignKPI.Flavour] = '#';
        newData[CampaignKPI.Name] = this.t('table:table.campaignSums');
        if (searchterm !== '') {
            this.channelSumRowIsChartable = false;
            newData[KPIGroupKey.CampaignId] = this.t('table:table.allSearchterm', {searchterm});
            return newData;
        }
        this.channelSumRowIsChartable = true;
        return newData;
    }
}

export default CampaignSelectorTableStore;



// WEBPACK FOOTER //
// ./src/stores/campaign/CampaignSelectorTableStore.ts